import classNames from 'classnames';


import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import MarkdownComponent from 'containers/user/learn/components/markdown/markdown';
import ErrorWidget from 'containers/user/home/components/widgets/error-widget';
import NavButton from 'containers/user/home/components/shared/navigation-button';

import { formatDate } from 'helpers/dates-formatter';

interface ReleaseNotesConfig {
  colSpan: number;
  rowSpan: Record<string, number>;
}

export const getReleaseNotesConfig = (withImage: boolean): ReleaseNotesConfig =>
  withImage
    ? {
        colSpan: 2,
        rowSpan: {
          default: 9,
          mobile: 14,
        },
      }
    : {
        colSpan: 1,
        rowSpan: {
          default: 9,
        },
      };

interface SkeletonLoaderProps {
  withImage?: boolean;
}

const SkeletonLoader = ({
  withImage,
}: SkeletonLoaderProps): React.JSX.Element => (
  <div
    role="progressbar"
    className={classNames(
      'h-full w-full p-5 lg:py-3 bg-gray-100 rounded-lg animate-pulse flex',
      withImage ? 'flex-row gap-x-5' : 'flex-col'
    )}
  >
    {!withImage && (
      <Box
        sx={{
          height: '100%',
          width: '50%',
          bgcolor: 'tokens.gray.300',
          borderRadius: '0.25rem',
        }}
      />
    )}
    <Stack sx={{ flex: '1 1 0%', rowGap: 1 }}>
      <Box
        sx={{
          height: '1.5rem',
          width: '25%',
          bgcolor: 'tokens.gray.300',
          borderRadius: '0.25rem',
          mb: 1,
        }}
      />
      <Box
        sx={{
          height: '1rem',
          width: '75%',
          bgcolor: 'tokens.gray.300',
          borderRadius: '0.25rem',
        }}
      />
      <Box
        sx={{
          height: '1rem',
          width: '100%',
          bgcolor: 'tokens.gray.300',
          borderRadius: '0.25rem',
        }}
      />
      <Box
        sx={{
          height: '1rem',
          width: '66.666667%',
          bgcolor: 'tokens.gray.300',
          borderRadius: '0.25rem',
        }}
      />
      <Box
        sx={{
          height: '2rem',
          width: '8rem',
          bgcolor: 'tokens.gray.300',
          borderRadius: '0.25rem',
          mt: 'auto',
        }}
      />
    </Stack>
  </div>
);

interface ReleaseNotesAttributes {
  releaseNotesDate?: string;
  image?: {
    imageSrc: { data: { attributes: { url: string } } };
  };
  releaseNotes?: string;
  targetLink?: string;
  title?: string;
}

interface ReleaseNotesData {
  attributes?: ReleaseNotesAttributes;
}

interface ReleaseNotesProps {
  data?: ReleaseNotesData;
  loading?: boolean;
  error?: unknown;
  hasImage?: boolean;
}

const ReleaseNotes = ({
  data,
  loading,
  error,
  hasImage,
}: ReleaseNotesProps): React.JSX.Element => {
  if (loading) {
    return <SkeletonLoader withImage={hasImage} />;
  }

  const {
    releaseNotesDate,
    image: releaseImage,
    releaseNotes,
    targetLink,
    title,
  } = data?.attributes || {};

  if (error || !releaseNotes) {
    return (
      <Stack
        sx={{
          p: 2.5,
          pt: 2,
          borderRadius: '0.5rem',
          height: '100%',
          width: '100%',
          justifyContent: 'center',
          borderWidth: '1px',
          borderColor: 'tokens.navy.200',
        }}
      >
        <h3 className="font-bold text-xl text-navy-800 mb-2.5">{title}</h3>
        <ErrorWidget />
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        p: 2.5,
        pt: 2,
        bgcolor: 'tokens.blue.100',
        borderRadius: '0.5rem',
        height: '100%',
        width: '100%',
        justifyContent: 'space-between',
      }}
    >
      <Stack
        direction="row"
        sx={{ alignItems: 'flex-start', flexWrap: 'wrap' }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            mb: 1.25,
          }}
        >
          <h3 className="font-bold text-xl text-navy-800">{title}</h3>
          <p className="font-sm font-semibold text-navy-500">
            {formatDate(releaseNotesDate, 'MMMM D, YYYY')}
          </p>
        </Stack>

        {releaseImage && (
          <img
            src={releaseImage.imageSrc.data.attributes.url}
            alt="Release Notes"
            className=" rounded-md w-[285px] h-[194px] mr-4 mb-2"
          />
        )}

        <Stack sx={{ flex: '1 1 0%', gap: 1 }}>
          <Box sx={{ color: 'tokens.navy.800' }}>
            <MarkdownComponent text={releaseNotes} />
          </Box>
        </Stack>
      </Stack>
      <NavButton link={targetLink} text="Read more" />
    </Stack>
  );
};

export default ReleaseNotes;
